import React, { useState } from 'react';
import LocationForm from '../components/LocationForm';
import TemplateManager from '../components/TemplateManager';
import SearchableSelect from '../components/SearchableSelect';
import { STATES, APPLICATION_METHODS, DEDUCTIBLE_TYPES } from '../utils/constants';
import { calculatePremium, downloadSettingsXlsx } from '../services/api';
import JSZip from 'jszip';

const WDBBSatinwood = () => {
  const [loading, setLoading] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Получаем даты по умолчанию
  const today = new Date();
  const nextYear = new Date(today);
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  const [versionNumber, setVersionNumber] = useState('1.0.0');
  
  const [formData, setFormData] = useState({
    policy: {
      state: '',
      effectiveDate: formatDate(today),
      expirationDate: formatDate(nextYear),
      lineWDBB: {
        applicationMethod: 'perBuilding',
        deductibleType: 'percent',
        overlyingDeductiblePercent: '',
        overlyingDeductibleMinimum: '',
        overlyingDeductibleFlat: '',
        retentionType: 'flat',
        retentionPercent: '',
        retentionMinimum: '',
        retentionFlat: ''
      },
      lossHistory: []
    },
    account: {
      locations: []
    }
  });

  const handlePolicyChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData({
        ...formData,
        policy: {
          ...formData.policy,
          [parent]: {
            ...formData.policy[parent],
            [child]: value
          }
        }
      });
    } else {
      setFormData({
        ...formData,
        policy: {
          ...formData.policy,
          [field]: value
        }
      });
    }
  };

  const handleLocationChange = (index, updatedLocation) => {
    const locations = [...formData.account.locations];
    locations[index] = updatedLocation;
    setFormData({
      ...formData,
      account: { ...formData.account, locations }
    });
  };

  const handleAddLocation = () => {
    setFormData({
      ...formData,
      account: {
        ...formData.account,
        locations: [
          ...formData.account.locations,
          { 
            id: Date.now().toString(),
            county: '',
            distanceToCoast: '',
            buildings: []
          }
        ]
      }
    });
  };

  const handleRemoveLocation = (index) => {
    const locations = formData.account.locations.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      account: { ...formData.account, locations }
    });
  };

  const handleLoadTemplate = (templateData) => {
    // Проверяем, есть ли в темплейте версия
    if (templateData.versionNumber) {
      setVersionNumber(templateData.versionNumber);
    }
    // Загружаем остальные данные формы
    const { versionNumber, ...formDataOnly } = templateData;
    setFormData(formDataOnly);
    setResult(null);
    setError(null);
    setLastRequest(null);
  };

  const handleLossHistoryChange = (lossCount) => {
    const losses = [];
    for (let i = 0; i < lossCount; i++) {
      losses.push({ 
        date: new Date().toISOString().split('T')[0], 
        amount: 1000 
      });
    }
    setFormData({
      ...formData,
      policy: {
        ...formData.policy,
        lossHistory: losses
      }
    });
  };

  const formatDataForAPI = () => {
    const { policy, account } = formData;
    const { lineWDBB } = policy;
    
    // Форматирование данных о локациях и зданиях
    const formattedLocations = {};
    account.locations.forEach((location, index) => {
      const locationId = `id${index + 1}`;
      const formattedBuildings = {};
      
      (location.buildings || []).forEach((building, buildingIndex) => {
        const buildingId = `id${buildingIndex + 1}`;
        formattedBuildings[buildingId] = {
          id: (buildingIndex + 1).toString(),
          ...building
        };
      });
      
      formattedLocations[locationId] = {
        id: (index + 1).toString(),
        county: location.county,
        distanceToCoast: parseFloat(location.distanceToCoast) || 0,
        buildings: formattedBuildings
      };
    });

    // Форматирование deductible данных
    const wdbbData = {
      applicationMethod: lineWDBB.applicationMethod
    };

    // Overlying deductible
    if (lineWDBB.deductibleType === 'percent') {
      wdbbData.overlyingDeductiblePercent = parseFloat(lineWDBB.overlyingDeductiblePercent) || 0;
    } else if (lineWDBB.deductibleType === 'percentWithMin') {
      wdbbData.overlyingDeductiblePercent = parseFloat(lineWDBB.overlyingDeductiblePercent) || 0;
      wdbbData.overlyingDeductibleMinimum = parseFloat(lineWDBB.overlyingDeductibleMinimum) || 0;
    } else {
      wdbbData.overlyingDeductibleFlat = parseFloat(lineWDBB.overlyingDeductibleFlat) || 0;
    }

    // Retention
    if (lineWDBB.retentionType === 'percent') {
      wdbbData.retentionPercent = parseFloat(lineWDBB.retentionPercent) || 0;
    } else if (lineWDBB.retentionType === 'percentWithMin') {
      wdbbData.retentionPercent = parseFloat(lineWDBB.retentionPercent) || 0;
      wdbbData.retentionMinimum = parseFloat(lineWDBB.retentionMinimum) || 0;
    } else {
      wdbbData.retentionFlat = parseFloat(lineWDBB.retentionFlat) || 0;
    }

    return {
      policy: {
        state: policy.state,
        effectiveDate: policy.effectiveDate,
        expirationDate: policy.expirationDate,
        lineWDBB: wdbbData,
        lossHistory: policy.lossHistory
      },
      account: {
        location: formattedLocations
      }
    };
  };

  const [lastRequest, setLastRequest] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const apiData = formatDataForAPI();
      const fullRequest = {
        version: {
          type: '12',
          number: versionNumber
        },
        ...apiData
      };
      
      setLastRequest(fullRequest);
      const response = await calculatePremium('12', versionNumber, apiData);
      
      if (response.success) {
        setResult(response.data);
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError('An unexpected error occurred: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateResultsHTML = (result, timestamp) => {
    if (!result || result._status !== 'ok') {
      return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>WDBB Calculation Results - ${timestamp}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; background-color: #f5f5f5; }
        .container { max-width: 1200px; margin: 0 auto; background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .header { border-bottom: 2px solid #007bff; padding-bottom: 15px; margin-bottom: 20px; }
        .error { background-color: #f8d7da; color: #721c24; padding: 15px; border-radius: 4px; border: 1px solid #f5c6cb; }
        .timestamp { color: #666; font-size: 0.9em; }
        pre { background-color: #f8f9fa; padding: 15px; border-radius: 4px; overflow-x: auto; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>WDBB Calculation Results</h1>
            <div class="timestamp">Generated: ${new Date().toLocaleString()}</div>
        </div>
        <div class="error">
            <strong>Error:</strong> Unable to generate detailed results. Please check the response data.
        </div>
        <h3>Raw Response</h3>
        <pre>${JSON.stringify(result, null, 2)}</pre>
    </div>
</body>
</html>`;
    }

    const premium = result.policy?.lineWDBB?._rated?.premium;
    const applicationMethod = result.policy?.lineWDBB?._rated?.applicationMethod;
    const hasLossHistory = result.policy?.lineWDBB?._rated?.hasLossHistory;
    const details = result.policy?.lineWDBB?._rated?.details || [];

    const detailsHTML = details.map((detail, index) => {
      if (detail.error) {
        return `
        <div class="detail-section error-section">
            <h4>Detail ${index + 1}</h4>
            ${detail.buildingId ? `<h5>Building: ${detail.buildingId}</h5>` : ''}
            ${detail.locationId ? `<h5>Location: ${detail.locationId}</h5>` : ''}
            ${detail.county ? `<h5>County: ${detail.county}</h5>` : ''}
            <div class="error-content">
                <p>${detail.error}</p>
                ${detail.requiresPriorSubmit ? '<p class="warning"><strong>Prior Submit Required</strong></p>' : ''}
            </div>
        </div>`;
      }

      const tiv = detail.tiv || detail.locationTIV || detail.countyTIV || 0;
      const baseRate = detail.baseRate || detail.weightedRate || 0;
      const finalRate = detail.finalRate || detail.weightedRate || 0;
      const detailPremium = detail.premium || 0;

      let factorsHTML = '';
      if (detail.factors) {
        factorsHTML = `
        <div class="factors-section">
            <h6>Building Factors:</h6>
            <ul>
                <li>Roof Type: ${(detail.factors.roofType * 100).toFixed(1)}%</li>
                <li>Roof Age: ${(detail.factors.roofAge * 100).toFixed(1)}%</li>
                <li>Building Age: ${(detail.factors.buildingAge * 100).toFixed(1)}%</li>
                <li>Distance to Coast: ${(detail.factors.distanceToCoast * 100).toFixed(1)}%</li>
                <li>Occupancy: ${(detail.factors.occupancy * 100).toFixed(1)}%</li>
                <li>Loss History: ${detail.factors.lossHistory ? `${(detail.factors.lossHistory * 100).toFixed(1)}%` : '0.0%'}</li>
                <li>Retention Factor: ${((detail.retentionFactor || 0) * 100).toFixed(1)}%</li>
                <li><strong>Total Building Factors: ${(detail.factors.total * 100).toFixed(1)}%</strong></li>
                <li><strong>Total Factor (incl. Retention): ${((detail.totalFactor || 0) * 100).toFixed(1)}%</strong></li>
            </ul>
        </div>`;
      }

      let stepsHTML = '';
      if (detail.calculationSteps && detail.calculationSteps.length > 0) {
        const stepsTableHTML = detail.calculationSteps.map(step => `
          <tr>
              <td>${step.step}</td>
              <td>${step.description}</td>
              <td>
                  ${typeof step.value === 'number' 
                      ? (step.value >= -1 && step.value <= 1 
                          ? (step.value * 100).toFixed(1) + '%'
                          : step.value.toLocaleString())
                      : step.value}
              </td>
              <td>${step.formula || '-'}</td>
          </tr>
        `).join('');

        stepsHTML = `
        <details class="calculation-steps">
            <summary>View Calculation Steps</summary>
            <table class="steps-table">
                <thead>
                    <tr>
                        <th>Step</th>
                        <th>Description</th>
                        <th>Value</th>
                        <th>Formula</th>
                    </tr>
                </thead>
                <tbody>
                    ${stepsTableHTML}
                </tbody>
            </table>
        </details>`;
      }

      let buildingsHTML = '';
      if (detail.buildings && detail.buildings.length > 0) {
        const buildingsList = detail.buildings.map((building, bIndex) => {
          let buildingFactorsHTML = '';
          if (building.factors) {
            buildingFactorsHTML = `
            <li>Factors:
                <ul>
                    <li>Roof Type: ${(building.factors.roofType * 100).toFixed(1)}%</li>
                    <li>Roof Age: ${(building.factors.roofAge * 100).toFixed(1)}%</li>
                    <li>Building Age: ${(building.factors.buildingAge * 100).toFixed(1)}%</li>
                    <li>Distance to Coast: ${(building.factors.distanceToCoast * 100).toFixed(1)}%</li>
                    <li>Occupancy: ${(building.factors.occupancy * 100).toFixed(1)}%</li>
                    <li>Loss History: ${building.factors.lossHistory ? `${(building.factors.lossHistory * 100).toFixed(1)}%` : '0.0%'}</li>
                    <li>Retention Factor: ${((building.retentionFactor || 0) * 100).toFixed(1)}%</li>
                    <li><strong>Total: ${(building.factors.total * 100).toFixed(1)}%</strong></li>
                </ul>
            </li>`;
          }

          return `
          <div class="building-item">
              <p><strong>Building ${building.buildingId}:</strong></p>
              <ul>
                  <li>TIV: $${building.tiv?.toLocaleString()}</li>
                  <li>Base Rate: ${building.baseRate?.toFixed(2)}%</li>
                  <li>Building Rate: ${building.buildingRate?.toFixed(2)}%</li>
                  ${buildingFactorsHTML}
              </ul>
          </div>`;
        }).join('');

        buildingsHTML = `
        <details class="building-details">
            <summary>View Building Details</summary>
            ${buildingsList}
        </details>`;
      }

      return `
      <div class="detail-section">
          <h4>Detail ${index + 1}</h4>
          ${detail.buildingId ? `<h5>Building: ${detail.buildingId}</h5>` : ''}
          ${detail.locationId ? `<h5>Location: ${detail.locationId}</h5>` : ''}
          ${detail.county ? `<h5>County: ${detail.county}</h5>` : ''}
          
          <div class="detail-summary">
              <p><strong>TIV:</strong> $${tiv.toLocaleString()}</p>
              <p><strong>Base Rate:</strong> ${baseRate.toFixed(2)}%</p>
              <p><strong>Final Rate:</strong> ${finalRate.toFixed(2)}%</p>
              <p><strong>Premium:</strong> $${detailPremium.toFixed(2)}</p>
              ${detail.overlying ? `<p><strong>Total Limit:</strong> $${detail.overlying.toLocaleString()}</p>` : ''}
              ${detail.retention ? `<p><strong>Final Limit:</strong> $${detail.retention.toLocaleString()}</p>` : ''}
              ${detail.maxRecoverable ? `<p><strong>Maximum Recoverable:</strong> $${detail.maxRecoverable.toLocaleString()}</p>` : ''}
              ${factorsHTML}
          </div>
          
          ${stepsHTML}
          ${buildingsHTML}
      </div>`;
    }).join('');

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>WDBB Calculation Results - ${timestamp}</title>
    <style>
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; 
            margin: 20px; 
            background-color: #f5f5f5; 
            line-height: 1.6;
        }
        .container { 
            max-width: 1200px; 
            margin: 0 auto; 
            background: white; 
            padding: 30px; 
            border-radius: 8px; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.1); 
        }
        .header { 
            border-bottom: 2px solid #007bff; 
            padding-bottom: 15px; 
            margin-bottom: 30px; 
        }
        .header h1 { 
            color: #007bff; 
            margin: 0 0 10px 0; 
        }
        .timestamp { 
            color: #666; 
            font-size: 0.9em; 
        }
        .summary-box { 
            background-color: #e7f3ff; 
            border: 1px solid #b3d7ff; 
            border-radius: 6px; 
            padding: 20px; 
            margin-bottom: 30px; 
        }
        .summary-box h4 { 
            margin-top: 0; 
            color: #0056b3; 
        }
        .summary-box p { 
            margin: 8px 0; 
            font-size: 1.1em; 
        }
        .detail-section { 
            border: 1px solid #ddd; 
            border-radius: 6px; 
            margin-bottom: 25px; 
            background: #fafafa; 
        }
        .detail-section h4, .detail-section h5 { 
            background: #f8f9fa; 
            margin: 0; 
            padding: 12px 20px; 
            border-bottom: 1px solid #ddd; 
        }
        .detail-summary { 
            padding: 20px; 
        }
        .detail-summary p { 
            margin: 8px 0; 
        }
        .factors-section { 
            background: white; 
            padding: 15px; 
            border-radius: 4px; 
            margin-top: 15px; 
        }
        .factors-section h6 { 
            margin-top: 0; 
            color: #495057; 
        }
        .factors-section ul { 
            margin: 10px 0; 
            padding-left: 20px; 
        }
        .factors-section li { 
            margin: 5px 0; 
        }
        .error-section { 
            border-color: #dc3545; 
        }
        .error-content { 
            background-color: #f8d7da; 
            color: #721c24; 
            padding: 15px 20px; 
            border-radius: 4px; 
        }
        .warning { 
            color: #856404 !important; 
            font-weight: bold; 
        }
        details { 
            margin: 15px 0; 
        }
        details summary { 
            background: #e9ecef; 
            padding: 10px 15px; 
            border-radius: 4px; 
            cursor: pointer; 
            font-weight: 500; 
        }
        details summary:hover { 
            background: #dee2e6; 
        }
        .steps-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 15px; 
        }
        .steps-table th, .steps-table td { 
            border: 1px solid #ddd; 
            padding: 12px; 
            text-align: left; 
        }
        .steps-table th { 
            background-color: #f8f9fa; 
            font-weight: 600; 
        }
        .steps-table tr:nth-child(even) { 
            background-color: #f8f9fa; 
        }
        .building-item { 
            background: white; 
            margin: 15px 0; 
            padding: 15px; 
            border-radius: 4px; 
            border: 1px solid #e9ecef; 
        }
        .building-item ul { 
            margin: 10px 0; 
        }
        .calculation-details h4 { 
            color: #495057; 
            margin-top: 30px; 
        }
        .raw-response { 
            margin-top: 30px; 
        }
        pre { 
            background-color: #f8f9fa; 
            padding: 20px; 
            border-radius: 6px; 
            overflow-x: auto; 
            font-size: 0.9em; 
            border: 1px solid #e9ecef; 
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>WDBB Calculation Results</h1>
            <div class="timestamp">Generated: ${new Date().toLocaleString()}</div>
        </div>
        
        <div class="summary-box">
            <h4>Summary</h4>
            <p><strong>Total Premium:</strong> $${premium?.toFixed(2) || '0.00'}</p>
            <p><strong>Application Method:</strong> ${applicationMethod || 'N/A'}</p>
            <p><strong>Has Loss History:</strong> ${hasLossHistory ? 'Yes' : 'No'}</p>
        </div>

        ${details.length > 0 ? `
        <div class="calculation-details">
            <h4>Calculation Details</h4>
            ${detailsHTML}
        </div>` : ''}

        <div class="raw-response">
            <details>
                <summary>View Full Response JSON</summary>
                <pre>${JSON.stringify(result, null, 2)}</pre>
            </details>
        </div>
    </div>
</body>
</html>`;
  };

  const downloadZipArchive = async (requestData, responseData, timestamp, isError = false) => {
    const zip = new JSZip();
    
    // Добавляем request JSON в архив
    const requestJson = JSON.stringify(requestData, null, 2);
    zip.file('request.json', requestJson);
    
    // Добавляем response или error JSON в архив
    if (isError) {
      const errorJson = JSON.stringify({ error: responseData }, null, 2);
      zip.file('error.json', errorJson);
    } else {
      const responseJson = JSON.stringify(responseData, null, 2);
      zip.file('response.json', responseJson);
      
      // Генерируем HTML файл с результатами (только при успешном расчете)
      const resultsHTML = generateResultsHTML(responseData, timestamp);
      zip.file('results.html', resultsHTML);
    }
    
    // Создаем import.json для кнопки Import
    const importTemplate = [
      {
        id: `calculation-${timestamp}`,
        name: `Calculation ${timestamp}`,
        createdAt: new Date().toISOString(),
        data: {
          ...formData,
          versionNumber: versionNumber
        }
      }
    ];
    const importJson = JSON.stringify(importTemplate, null, 2);
    zip.file('import.json', importJson);
    
    // Генерируем ZIP файл и скачиваем его
    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wdbb-calculation${isError ? '-error' : ''}-${timestamp}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCalculateAndDownload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    // Генерируем timestamp один раз для всех случаев
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);

    try {
      const apiData = formatDataForAPI();
      const fullRequest = {
        version: {
          type: '12',
          number: versionNumber
        },
        ...apiData
      };

      setLastRequest(fullRequest);
      const response = await calculatePremium('12', versionNumber, apiData);

      if (response.success) {
        setResult(response.data);

        // Скачиваем ZIP архив с request.json и response.json
        await downloadZipArchive(fullRequest, response.data, timestamp, false);
      } else {
        setError(response.error);

        // Скачиваем ZIP архив с request.json и error.json
        await downloadZipArchive(fullRequest, response.error, timestamp, true);
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred: ' + err.message;
      setError(errorMessage);

      // Скачиваем ZIP архив с request.json и error.json даже при исключении
      const apiData = formatDataForAPI();
      const fullRequest = {
        version: {
          type: '12',
          number: versionNumber
        },
        ...apiData
      };

      await downloadZipArchive(fullRequest, errorMessage, timestamp, true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSettingsXlsx = async () => {
    setDownloadingXlsx(true);
    setError(null);

    try {
      const response = await downloadSettingsXlsx('12', versionNumber);

      if (!response.success) {
        setError(response.error);
      }
    } catch (err) {
      setError('Failed to download settings: ' + err.message);
    } finally {
      setDownloadingXlsx(false);
    }
  };

  return (
    <div>
      <h1>WDBB Satinwood Calculator</h1>
      <p className="text-muted">Wind/Hail Buydown premium calculator</p>

      <TemplateManager 
        currentData={{ ...formData, versionNumber }}
        onLoadTemplate={handleLoadTemplate}
        templateType="wdbb-satinwood"
      />

      <form onSubmit={handleSubmit}>
        <div className="card">
          <h2>Configuration</h2>
          <div className="form-group">
            <label>API Version Number</label>
            <input
              type="text"
              className="form-control"
              value={versionNumber}
              onChange={(e) => setVersionNumber(e.target.value)}
              placeholder="e.g., 1.0.0"
              pattern="^\d+\.\d+\.\d+(-.*)?$"
              title="Version must be in format X.Y.Z or X.Y.Z-label"
            />
            <small className="text-muted">
              Format: major.minor.patch (e.g., 1.0.0) or with label (e.g., 1.0.0-beta)
            </small>
          </div>
          <div className="form-group">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleDownloadSettingsXlsx}
              disabled={downloadingXlsx || !versionNumber}
              style={{ marginBottom: '10px' }}
            >
              {downloadingXlsx ? 'Downloading...' : 'Download Settings (XLSX)'}
            </button>
            <br />
            <small className="text-muted">
              Download rating configuration for version {versionNumber} as Excel file
            </small>
          </div>
        </div>

        <div className="card">
          <h2>Policy Information</h2>
          
          <div className="grid">
            <div className="form-group">
              <label>State *</label>
              <SearchableSelect
                options={STATES}
                value={formData.policy.state}
                onChange={(value) => handlePolicyChange('state', value)}
                placeholder="Select state..."
                required
              />
            </div>

            <div className="form-group">
              <label>Effective Date *</label>
              <input
                type="date"
                className="form-control"
                value={formData.policy.effectiveDate}
                onChange={(e) => handlePolicyChange('effectiveDate', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Expiration Date *</label>
              <input
                type="date"
                className="form-control"
                value={formData.policy.expirationDate}
                onChange={(e) => handlePolicyChange('expirationDate', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Number of Losses</label>
            <select
              className="form-control"
              value={formData.policy.lossHistory.length}
              onChange={(e) => handleLossHistoryChange(parseInt(e.target.value))}
            >
              <option value="0">No losses</option>
              <option value="1">1 loss</option>
              <option value="2">2 losses</option>
              <option value="3">3 losses (Prior Submit Required)</option>
              <option value="4">4+ losses (Prior Submit Required)</option>
            </select>
            <small className="text-muted d-block">
              Loss history affects roof age credits. 1 loss = +10%, 2 losses = +20%, 3+ losses = Prior Submit
            </small>
          </div>
        </div>

        <div className="card">
          <h2>Deductible Structure</h2>
          
          <div className="form-group">
            <label>Application Method *</label>
            <select
              className="form-control"
              value={formData.policy.lineWDBB.applicationMethod}
              onChange={(e) => handlePolicyChange('lineWDBB.applicationMethod', e.target.value)}
              required
            >
              {APPLICATION_METHODS.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid">
            <div>
              <h4>Overlying Deductible</h4>
              <div className="form-group">
                <label>Type *</label>
                <select
                  className="form-control"
                  value={formData.policy.lineWDBB.deductibleType}
                  onChange={(e) => handlePolicyChange('lineWDBB.deductibleType', e.target.value)}
                  required
                >
                  {DEDUCTIBLE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.policy.lineWDBB.deductibleType === 'percent' && (
                <div className="form-group">
                  <label>Percentage *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.policy.lineWDBB.overlyingDeductiblePercent}
                    onChange={(e) => handlePolicyChange('lineWDBB.overlyingDeductiblePercent', e.target.value)}
                    placeholder="Enter percentage"
                    min="0"
                    max="100"
                    step="0.1"
                    required
                  />
                </div>
              )}

              {formData.policy.lineWDBB.deductibleType === 'percentWithMin' && (
                <>
                  <div className="form-group">
                    <label>Percentage *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.policy.lineWDBB.overlyingDeductiblePercent}
                      onChange={(e) => handlePolicyChange('lineWDBB.overlyingDeductiblePercent', e.target.value)}
                      placeholder="Enter percentage"
                      min="0"
                      max="100"
                      step="0.1"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Minimum Amount *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.policy.lineWDBB.overlyingDeductibleMinimum}
                      onChange={(e) => handlePolicyChange('lineWDBB.overlyingDeductibleMinimum', e.target.value)}
                      placeholder="Enter minimum"
                      min="0"
                      required
                    />
                  </div>
                </>
              )}

              {formData.policy.lineWDBB.deductibleType === 'flat' && (
                <div className="form-group">
                  <label>Flat Amount *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.policy.lineWDBB.overlyingDeductibleFlat}
                    onChange={(e) => handlePolicyChange('lineWDBB.overlyingDeductibleFlat', e.target.value)}
                    placeholder="Enter amount"
                    min="0"
                    required
                  />
                </div>
              )}
            </div>

            <div>
              <h4>Retention</h4>
              <div className="form-group">
                <label>Type *</label>
                <select
                  className="form-control"
                  value={formData.policy.lineWDBB.retentionType}
                  onChange={(e) => handlePolicyChange('lineWDBB.retentionType', e.target.value)}
                  required
                >
                  {DEDUCTIBLE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.policy.lineWDBB.retentionType === 'percent' && (
                <div className="form-group">
                  <label>Percentage *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.policy.lineWDBB.retentionPercent}
                    onChange={(e) => handlePolicyChange('lineWDBB.retentionPercent', e.target.value)}
                    placeholder="Enter percentage"
                    min="0"
                    max="100"
                    step="0.1"
                    required
                  />
                </div>
              )}

              {formData.policy.lineWDBB.retentionType === 'percentWithMin' && (
                <>
                  <div className="form-group">
                    <label>Percentage *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.policy.lineWDBB.retentionPercent}
                      onChange={(e) => handlePolicyChange('lineWDBB.retentionPercent', e.target.value)}
                      placeholder="Enter percentage"
                      min="0"
                      max="100"
                      step="0.1"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Minimum Amount *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.policy.lineWDBB.retentionMinimum}
                      onChange={(e) => handlePolicyChange('lineWDBB.retentionMinimum', e.target.value)}
                      placeholder="Enter minimum"
                      min="0"
                      required
                    />
                  </div>
                </>
              )}

              {formData.policy.lineWDBB.retentionType === 'flat' && (
                <div className="form-group">
                  <label>Flat Amount *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.policy.lineWDBB.retentionFlat}
                    onChange={(e) => handlePolicyChange('lineWDBB.retentionFlat', e.target.value)}
                    placeholder="Enter amount"
                    min="0"
                    required
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="flex-between mb-3">
            <h2>Locations & Buildings</h2>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleAddLocation}
            >
              Add Location
            </button>
          </div>

          {formData.account.locations.map((location, index) => (
            <LocationForm
              key={index}
              location={location}
              onChange={handleLocationChange}
              onRemove={handleRemoveLocation}
              index={index}
              selectedState={formData.policy.state}
            />
          ))}

          {formData.account.locations.length === 0 && (
            <div className="card">
              <p className="text-muted">No locations added. Click "Add Location" to start.</p>
            </div>
          )}
        </div>

        <div className="mt-3">
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading || formData.account.locations.length === 0}
          >
            {loading ? 'Calculating...' : 'Calculate Premium'}
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            style={{ marginLeft: '10px' }}
            disabled={loading || formData.account.locations.length === 0}
            onClick={handleCalculateAndDownload}
          >
            {loading ? 'Calculating...' : 'Calculate & Download'}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            style={{ marginLeft: '10px' }}
            onClick={() => {
              setVersionNumber('1.0.0');
              setFormData({
                policy: {
                  state: '',
                  effectiveDate: formatDate(today),
                  expirationDate: formatDate(nextYear),
                  lineWDBB: {
                    applicationMethod: 'perBuilding',
                    deductibleType: 'percent',
                    overlyingDeductiblePercent: '',
                    overlyingDeductibleMinimum: '',
                    overlyingDeductibleFlat: '',
                    retentionType: 'flat',
                    retentionPercent: '',
                    retentionMinimum: '',
                    retentionFlat: ''
                  },
                  lossHistory: []
                },
                account: {
                  locations: []
                }
              });
              setResult(null);
              setError(null);
              setLastRequest(null);
            }}
          >
            Reset Form
          </button>
        </div>
      </form>

      {error && (
        <div className="error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {lastRequest && (
        <div className="results">
          <h3>Request JSON</h3>
          <details open>
            <summary>View Request Data</summary>
            <pre>{JSON.stringify(lastRequest, null, 2)}</pre>
          </details>
        </div>
      )}

      {result && (
        <div className="results">
          <h3>Calculation Results</h3>
          {result._status === 'ok' ? (
            <div>
              <div className="summary-box">
                <h4>Summary</h4>
                <p><strong>Total Premium:</strong> ${result.policy?.lineWDBB?._rated?.premium?.toFixed(2) || '0.00'}</p>
                <p><strong>Application Method:</strong> {result.policy?.lineWDBB?._rated?.applicationMethod || 'N/A'}</p>
                <p><strong>Has Loss History:</strong> {result.policy?.lineWDBB?._rated?.hasLossHistory ? 'Yes' : 'No'}</p>
              </div>

              {result.policy?.lineWDBB?._rated?.details && result.policy.lineWDBB._rated.details.length > 0 && (
                <div className="calculation-details">
                  <h4>Calculation Details</h4>
                  {result.policy.lineWDBB._rated.details.map((detail, index) => (
                    <div key={index} className="detail-section">
                      {detail.buildingId && <h5>Building: {detail.buildingId}</h5>}
                      {detail.locationId && <h5>Location: {detail.locationId}</h5>}
                      {detail.county && <h5>County: {detail.county}</h5>}
                      
                      {detail.error ? (
                        <div className="error">
                          <p>{detail.error}</p>
                          {detail.requiresPriorSubmit && <p className="warning">Prior Submit Required</p>}
                        </div>
                      ) : (
                        <>
                          <div className="detail-summary">
                            <p><strong>TIV:</strong> ${detail.tiv?.toLocaleString() || detail.locationTIV?.toLocaleString() || detail.countyTIV?.toLocaleString() || '0'}</p>
                            <p><strong>Base Rate:</strong> {detail.baseRate?.toFixed(2) || detail.weightedRate?.toFixed(2) || '0.00'}%</p>
                            <p><strong>Final Rate:</strong> {detail.finalRate?.toFixed(2) || detail.weightedRate?.toFixed(2) || '0.00'}%</p>
                            <p><strong>Premium:</strong> ${detail.premium?.toFixed(2) || '0.00'}</p>
                            {detail.overlying && (
                              <p><strong>Total Limit:</strong> ${detail.overlying.toLocaleString()}</p>
                            )}
                            {detail.retention && (
                              <p><strong>Final Limit:</strong> ${detail.retention.toLocaleString()}</p>
                            )}
                            {detail.maxRecoverable && (
                              <p><strong>Maximum Recoverable:</strong> ${detail.maxRecoverable.toLocaleString()}</p>
                            )}
                            {detail.factors && (
                              <div>
                                <p><strong>Building Factors:</strong></p>
                                <ul style={{paddingLeft: '20px', marginTop: '5px'}}>
                                  <li>Roof Type: {(detail.factors.roofType * 100).toFixed(1)}%</li>
                                  <li>Roof Age: {(detail.factors.roofAge * 100).toFixed(1)}%</li>
                                  <li>Building Age: {(detail.factors.buildingAge * 100).toFixed(1)}%</li>
                                  <li>Distance to Coast: {(detail.factors.distanceToCoast * 100).toFixed(1)}%</li>
                                  <li>Occupancy: {(detail.factors.occupancy * 100).toFixed(1)}%</li>
                                  <li>Loss History: {detail.factors.lossHistory ? `${(detail.factors.lossHistory * 100).toFixed(1)}%` : '0.0%'}</li>
                                  <li>Retention Factor: {((detail.retentionFactor || 0) * 100).toFixed(1)}%</li>
                                  <li><strong>Total Building Factors: {(detail.factors.total * 100).toFixed(1)}%</strong></li>
                                  <li><strong>Total Factor (incl. Retention): {((detail.totalFactor || 0) * 100).toFixed(1)}%</strong></li>
                                </ul>
                              </div>
                            )}
                          </div>

                          {detail.calculationSteps && detail.calculationSteps.length > 0 && (
                            <details className="calculation-steps">
                              <summary>View Calculation Steps</summary>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Step</th>
                                    <th>Description</th>
                                    <th>Value</th>
                                    <th>Formula</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detail.calculationSteps.map((step) => (
                                    <tr key={step.step}>
                                      <td>{step.step}</td>
                                      <td>{step.description}</td>
                                      <td>
                                        {typeof step.value === 'number' 
                                          ? (step.value >= -1 && step.value <= 1 
                                              ? (step.value * 100).toFixed(1) + '%'
                                              : step.value.toLocaleString())
                                          : step.value}
                                      </td>
                                      <td className="formula-cell">{step.formula || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </details>
                          )}

                          {detail.buildings && detail.buildings.length > 0 && (
                            <details className="building-details">
                              <summary>View Building Details</summary>
                              {detail.buildings.map((building, bIndex) => (
                                <div key={bIndex} className="building-item">
                                  <p><strong>Building {building.buildingId}:</strong></p>
                                  <ul>
                                    <li>TIV: ${building.tiv?.toLocaleString()}</li>
                                    <li>Base Rate: {building.baseRate?.toFixed(2)}%</li>
                                    <li>Building Rate: {building.buildingRate?.toFixed(2)}%</li>
                                    {building.factors && (
                                      <>
                                        <li>Factors:
                                          <ul>
                                            <li>Roof Type: {(building.factors.roofType * 100).toFixed(1)}%</li>
                                            <li>Roof Age: {(building.factors.roofAge * 100).toFixed(1)}%</li>
                                            <li>Building Age: {(building.factors.buildingAge * 100).toFixed(1)}%</li>
                                            <li>Distance to Coast: {(building.factors.distanceToCoast * 100).toFixed(1)}%</li>
                                            <li>Occupancy: {(building.factors.occupancy * 100).toFixed(1)}%</li>
                                            <li>Loss History: {building.factors.lossHistory ? `${(building.factors.lossHistory * 100).toFixed(1)}%` : '0.0%'}</li>
                                            <li>Retention Factor: {((building.retentionFactor || 0) * 100).toFixed(1)}%</li>
                                            <li><strong>Total: {(building.factors.total * 100).toFixed(1)}%</strong></li>
                                          </ul>
                                        </li>
                                      </>
                                    )}
                                  </ul>
                                </div>
                              ))}
                            </details>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <details>
                <summary>View Full Response JSON</summary>
                <pre>{JSON.stringify(result, null, 2)}</pre>
              </details>
            </div>
          ) : (
            <pre>{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
};

export default WDBBSatinwood;